import clsx from 'clsx';
import React, { useState } from 'react';

import { TroubleshootingTips } from '@/components/troubleshootingTips/troubleshootingTips';
import { Heading } from '@/components/typography/heading';
import { ContractStatus } from '@/types/dataContracts';

import styles from '../../contractDetails.module.scss';
import { ErrorDetails } from './ErrorDetails';
import { MetadataRow } from './MetadataRow';
import { ProcessingStatus } from './ProcessingStatus';
import { TermsAndConditions } from './TermsAndConditions';

interface StatusConfig {
  icon: React.ReactNode;
  label: string;
  description: string;
}

interface LatestTask {
  task_id: string;
  task_type: string;
  error?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
}

interface ContractStatusData {
  latest_task?: LatestTask | null;
}

interface TaskStatus {
  error?: string | null;
}

interface ParsedYAML {
  info?: {
    contact?: any;
    tenant?: string;
  };
  terms?: {
    usage?: string;
    limitations?: string;
  };
}

interface Contract {
  status: ContractStatus;
  name: string;
  contract_id: string;
}

interface PropertiesSectionProps {
  contract: Contract;
  config: StatusConfig | null;
  contractStatus?: ContractStatusData;
  taskStatus?: TaskStatus;
  parsedYAML: ParsedYAML | null;
  latestTaskId: string | null;
  troubleshootingTips: any[];
  formatContact: ( contact: any ) => string;
  onShowProcessingModal: () => void;
}

export function PropertiesSection( {
  contract,
  config,
  contractStatus,
  taskStatus,
  parsedYAML,
  latestTaskId,
  troubleshootingTips,
  formatContact,
  onShowProcessingModal
}: PropertiesSectionProps ) {
  const [ isErrorExpanded, setIsErrorExpanded ] = useState( false );

  const formatLastContractProcess = () => {
    if ( contractStatus?.latest_task?.completed_at || contractStatus?.latest_task?.started_at ) {
      const date = contractStatus.latest_task.completed_at || contractStatus.latest_task.started_at!;
      return new Date( date ).toLocaleString( 'en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      } );
    }
    return 'N/A';
  };

  const renderStatusContent = () => {
    if ( !config ) return null;

    switch ( contract.status ) {
      case ContractStatus.FAILED:
        return (
          <>
            <div className={ styles.failedStateBox }>
              <div className={ styles.failedSection }>
                <div className={ styles.failedTopSection }>
                  {/* Left: Processing Status */}
                  <div className={ styles.statusColumn }>
                    <ProcessingStatus
                      label={ config.label }
                      icon={ config.icon }
                      description={ config.description }
                    />
                  </div>

                  {/* Right: Error Details */}
                  <ErrorDetails
                    error={ contractStatus?.latest_task?.error || taskStatus?.error }
                    latestTask={ contractStatus?.latest_task }
                    contractName={ contract.name }
                    contractId={ contract.contract_id }
                    isExpanded={ isErrorExpanded }
                    onToggleExpand={ () => setIsErrorExpanded( !isErrorExpanded ) }
                  />
                </div>
              </div>

              <TroubleshootingTips tips={ troubleshootingTips } />
            </div>

            <MetadataRow
              lastDataSync="Pending"
              lastContractProcess={ formatLastContractProcess() }
              contact={ formatContact( parsedYAML?.info?.contact ) }
            />

            <div className={ styles.dividerLine } />

            <TermsAndConditions terms={ parsedYAML?.terms } />
          </>
        );

      case ContractStatus.QUEUED:
        return (
          <>
            <div className={ styles.queuedStateBox }>
              <ProcessingStatus
                label={ config.label }
                icon={ config.icon }
                description={ config.description }
                showEyeButton={ !!latestTaskId }
                onViewDetails={ onShowProcessingModal }
              />
            </div>

            <MetadataRow
              lastDataSync="Pending"
              lastContractProcess={ formatLastContractProcess() }
              contact={ formatContact( parsedYAML?.info?.contact ) }
            />
          </>
        );

      case ContractStatus.PROCESSING:
        return (
          <>
            <div className={ styles.processingStateBox }>
              <ProcessingStatus
                label={ config.label }
                icon={ config.icon }
                description={ config.description }
                showEyeButton={ !!latestTaskId }
                onViewDetails={ onShowProcessingModal }
              />
            </div>

            <MetadataRow
              lastDataSync="Pending"
              lastContractProcess={ formatLastContractProcess() }
              contact={ formatContact( parsedYAML?.info?.contact ) }
            />
          </>
        );

      case ContractStatus.COMPLETED:
        return (
          <>
            <div className={ styles.completedStateBox }>
              <ProcessingStatus
                label={ config.label }
                icon={ config.icon }
                description={ config.description }
                showEyeButton={ !!latestTaskId }
                onViewDetails={ onShowProcessingModal }
              />
            </div>

            <MetadataRow
              lastDataSync="Pending"
              lastContractProcess={ formatLastContractProcess() }
              contact={ formatContact( parsedYAML?.info?.contact ) }
            />
          </>
        );

      case ContractStatus.DELETING:
        return (
          <>
            <div className={ styles.deletingStateBox }>
              <ProcessingStatus
                label={ config.label }
                icon={ config.icon }
                description={ config.description }
              />
            </div>

            <MetadataRow
              lastDataSync="Pending"
              lastContractProcess={ formatLastContractProcess() }
              contact={ formatContact( parsedYAML?.info?.contact ) }
            />
          </>
        );

      default:
        // DRAFT state (fallback)
        return (
          <>
            <div className={ styles.subsection }>
              <ProcessingStatus
                label={ config.label }
                icon={ config.icon }
                description={ config.description }
              />
            </div>

            <MetadataRow
              lastDataSync="Pending"
              lastContractProcess={ formatLastContractProcess() }
              contact={ formatContact( parsedYAML?.info?.contact ) }
            />
          </>
        );
    }
  };

  return (
    <div className={ clsx( 'layout__row', 'pad' ) }>
      <div
        className={ clsx(
          styles.card,
          {
            [styles.failedCard]: contract.status === ContractStatus.FAILED,
            [styles.noBorderCard]: contract.status !== ContractStatus.FAILED
          }
        ) }
        data-testid='properties-card'
      >
        <div className={ styles.cardHeader }>
          <Heading as='h3' size='sm'>Properties</Heading>
        </div>
        <div className={ styles.cardBody }>
          { renderStatusContent() }
        </div>
      </div>
    </div>
  );
}
